/**
 * DEALSHAQ MULTIMEDIA CONTENT
 * Phase 4: Video Tutorials, GIF Demos, Interactive Walkthroughs
 * 
 * This file contains placeholder content for multimedia assets.
 * Videos and GIFs will be replaced with actual assets in production.
 * 
 * STRUCTURE:
 * - VIDEO_TUTORIALS: Full-length tutorial videos (2-5 min)
 * - GIF_DEMOS: Short animated demos (5-15 sec loops)
 * - INTERACTIVE_WALKTHROUGHS: Step-by-step guided tours
 */

// =============================================================================
// VIDEO TUTORIALS
// =============================================================================
export const VIDEO_TUTORIALS = {
  retailer: [
    {
      id: "retailer-getting-started",
      title: "Getting Started as a Retailer",
      description: "Complete walkthrough of setting up your store and posting your first deal",
      duration: "4:30",
      thumbnail: "/assets/tutorials/thumbnails/retailer-getting-started.jpg",
      videoUrl: null, // Placeholder - will be replaced with actual video URL
      placeholderType: "video",
      category: "onboarding",
      priority: 1,
      chapters: [
        { time: "0:00", title: "Introduction" },
        { time: "0:45", title: "Creating Your Account" },
        { time: "1:30", title: "Store Profile Setup" },
        { time: "2:15", title: "Connecting Stripe" },
        { time: "3:00", title: "Posting Your First Deal" },
        { time: "4:00", title: "Next Steps" }
      ],
      relatedDocs: ["retailer-onboarding", "retailer-item-posting"]
    },
    {
      id: "retailer-barcode-scanning",
      title: "Mastering the Barcode Scanner",
      description: "Learn all 7 scan modes and OCR features to post deals quickly",
      duration: "5:15",
      thumbnail: "/assets/tutorials/thumbnails/barcode-scanning.jpg",
      videoUrl: null,
      placeholderType: "video",
      category: "scanner",
      priority: 1,
      chapters: [
        { time: "0:00", title: "Scanner Overview" },
        { time: "0:30", title: "Single Scan Mode" },
        { time: "1:15", title: "Continuous Scan Mode" },
        { time: "2:00", title: "Photo & Multi-Image Modes" },
        { time: "3:00", title: "OCR Price Extraction" },
        { time: "4:00", title: "Queue System & Batch Posting" },
        { time: "4:45", title: "Tips & Best Practices" }
      ],
      relatedDocs: ["retailer-barcode-scanning"]
    },
    {
      id: "retailer-order-management",
      title: "Managing Orders Like a Pro",
      description: "Handle orders efficiently from acknowledgment to delivery",
      duration: "3:45",
      thumbnail: "/assets/tutorials/thumbnails/order-management.jpg",
      videoUrl: null,
      placeholderType: "video",
      category: "orders",
      priority: 2,
      chapters: [
        { time: "0:00", title: "Order Lifecycle Overview" },
        { time: "0:45", title: "Acknowledging Orders" },
        { time: "1:30", title: "Preparing & Marking Ready" },
        { time: "2:15", title: "Dispatching via DoorDash" },
        { time: "3:00", title: "Handling Issues & Cancellations" }
      ],
      relatedDocs: ["retailer-order-management"]
    },
    {
      id: "retailer-analytics",
      title: "Understanding Your Analytics",
      description: "Track performance, identify trends, and grow your business",
      duration: "3:00",
      thumbnail: "/assets/tutorials/thumbnails/analytics.jpg",
      videoUrl: null,
      placeholderType: "video",
      category: "analytics",
      priority: 3,
      chapters: [
        { time: "0:00", title: "Dashboard Overview" },
        { time: "0:45", title: "Sales Analytics" },
        { time: "1:30", title: "Inventory Metrics" },
        { time: "2:15", title: "KPIs & Performance" }
      ],
      relatedDocs: ["retailer-dashboard-analytics", "retailer-kpis"]
    }
  ],
  consumer: [
    {
      id: "consumer-getting-started",
      title: "Welcome to DealShaq",
      description: "Find sizzling hot deals on groceries and save money",
      duration: "3:00",
      thumbnail: "/assets/tutorials/thumbnails/consumer-getting-started.jpg",
      videoUrl: null,
      placeholderType: "video",
      category: "onboarding",
      priority: 1,
      chapters: [
        { time: "0:00", title: "What is DealShaq?" },
        { time: "0:45", title: "Finding Deals Near You" },
        { time: "1:30", title: "Using the AI Agent" },
        { time: "2:15", title: "Checkout & Delivery" }
      ],
      relatedDocs: ["consumer-shopping-checkout", "consumer-store-discovery"]
    },
    {
      id: "consumer-ai-agent",
      title: "Shopping with the AI Agent",
      description: "Let DealShaq find the sizzling hot deals for your grocery list",
      duration: "4:00",
      thumbnail: "/assets/tutorials/thumbnails/ai-agent.jpg",
      videoUrl: null,
      placeholderType: "video",
      category: "agent",
      priority: 1,
      chapters: [
        { time: "0:00", title: "Meet the DealShaq Shopping Assistant (DSA)" },
        { time: "0:45", title: "Submitting a Shopping List" },
        { time: "1:30", title: "Setting Constraints" },
        { time: "2:15", title: "Reviewing Sourced Items" },
        { time: "3:00", title: "Uploading Lists" },
        { time: "3:30", title: "Memory & Preferences" }
      ],
      relatedDocs: ["consumer-dealshaq-agent"]
    },
    {
      id: "consumer-multi-store",
      title: "Multi-Store Shopping",
      description: "Shop from multiple stores in a single order",
      duration: "2:30",
      thumbnail: "/assets/tutorials/thumbnails/multi-store.jpg",
      videoUrl: null,
      placeholderType: "video",
      category: "shopping",
      priority: 2,
      chapters: [
        { time: "0:00", title: "How Multi-Store Works" },
        { time: "0:45", title: "Adding Items from Different Stores" },
        { time: "1:30", title: "Managing Your Cart" },
        { time: "2:00", title: "Checkout & Delivery" }
      ],
      relatedDocs: ["consumer-store-discovery"]
    }
  ],
  admin: [
    {
      id: "admin-incident-resolution",
      title: "Resolving Incidents",
      description: "Quick guide to handling platform incidents",
      duration: "3:30",
      thumbnail: "/assets/tutorials/thumbnails/incidents.jpg",
      videoUrl: null,
      placeholderType: "video",
      category: "incidents",
      priority: 1,
      chapters: [
        { time: "0:00", title: "Incident Types" },
        { time: "0:45", title: "Severity Levels" },
        { time: "1:30", title: "Resolution Process" },
        { time: "2:30", title: "Escalation" }
      ],
      relatedDocs: ["admin-incident-management"]
    },
    {
      id: "admin-retailer-approval",
      title: "Retailer Approval Process",
      description: "How to review and approve retailer applications",
      duration: "2:45",
      thumbnail: "/assets/tutorials/thumbnails/retailer-approval.jpg",
      videoUrl: null,
      placeholderType: "video",
      category: "approvals",
      priority: 2,
      chapters: [
        { time: "0:00", title: "Application Queue" },
        { time: "0:45", title: "Verification Checklist" },
        { time: "1:45", title: "Approval Actions" }
      ],
      relatedDocs: ["admin-retailer-approvals"]
    }
  ],
  charity: [
    {
      id: "charity-dashboard-overview",
      title: "DealShaq Charity App Overview",
      description: "Track donations and manage your charity profile",
      duration: "2:30",
      thumbnail: "/assets/tutorials/thumbnails/charity-dashboard.jpg",
      videoUrl: null,
      placeholderType: "video",
      category: "dashboard",
      priority: 1,
      chapters: [
        { time: "0:00", title: "Dashboard Overview" },
        { time: "0:45", title: "Viewing Donations" },
        { time: "1:30", title: "Payout Settings" },
        { time: "2:00", title: "Impact Reports" }
      ],
      relatedDocs: ["charity-donation-dashboard", "charity-impact-metrics"]
    }
  ]
};

// =============================================================================
// GIF DEMOS (Micro-animations for quick feature explanations)
// =============================================================================
export const GIF_DEMOS = {
  retailer: [
    {
      id: "gif-single-scan",
      title: "Single Scan",
      description: "Point camera at barcode to scan",
      gifUrl: null, // Placeholder
      placeholderType: "gif",
      duration: "5s",
      category: "scanner",
      feature: "single-scan",
      steps: ["Point camera at barcode", "Wait for beep", "Item added to queue"]
    },
    {
      id: "gif-continuous-scan",
      title: "Continuous Scan",
      description: "Rapid-fire scanning without pausing",
      gifUrl: null,
      placeholderType: "gif",
      duration: "8s",
      category: "scanner",
      feature: "continuous-scan",
      steps: ["Enable continuous mode", "Scan multiple items", "All added automatically"]
    },
    {
      id: "gif-ocr-capture",
      title: "OCR Price Capture",
      description: "Auto-extract price from label",
      gifUrl: null,
      placeholderType: "gif",
      duration: "6s",
      category: "scanner",
      feature: "ocr",
      steps: ["Frame the price label", "OCR reads price", "Auto-filled in form"]
    },
    {
      id: "gif-acknowledge-order",
      title: "Acknowledge Order",
      description: "Confirm order within 5 minutes",
      gifUrl: null,
      placeholderType: "gif",
      duration: "4s",
      category: "orders",
      feature: "acknowledge",
      steps: ["Click Acknowledge", "Set ready-by time", "Order confirmed"]
    },
    {
      id: "gif-mark-ready",
      title: "Mark Order Ready",
      description: "Signal order is prepared",
      gifUrl: null,
      placeholderType: "gif",
      duration: "3s",
      category: "orders",
      feature: "mark-ready",
      steps: ["Click Mark Ready", "Payment captured", "Customer notified"]
    },
    {
      id: "gif-dispatch",
      title: "Dispatch Order",
      description: "Send order via DoorDash",
      gifUrl: null,
      placeholderType: "gif",
      duration: "4s",
      category: "orders",
      feature: "dispatch",
      steps: ["Click Dispatch", "DoorDash accepts", "Tracking active"]
    },
    {
      id: "gif-deal-levels",
      title: "Setting Deal Levels",
      description: "Choose discount tier for items",
      gifUrl: null,
      placeholderType: "gif",
      duration: "5s",
      category: "posting",
      feature: "deal-levels",
      steps: ["Select deal level", "See discount preview", "Confirm posting"]
    }
  ],
  consumer: [
    {
      id: "gif-add-to-cart",
      title: "Add to Cart",
      description: "Quick add items to your cart",
      gifUrl: null,
      placeholderType: "gif",
      duration: "3s",
      category: "shopping",
      feature: "add-to-cart",
      steps: ["Click Add to Cart", "Item added", "Cart updates"]
    },
    {
      id: "gif-agent-chat",
      title: "Chat with Agent",
      description: "Ask the AI for help",
      gifUrl: null,
      placeholderType: "gif",
      duration: "6s",
      category: "agent",
      feature: "chat",
      steps: ["Type your request", "Agent responds", "View suggestions"]
    },
    {
      id: "gif-upload-list",
      title: "Upload Shopping List",
      description: "Upload a file or photo",
      gifUrl: null,
      placeholderType: "gif",
      duration: "5s",
      category: "agent",
      feature: "upload",
      steps: ["Click Upload", "Select file", "Agent processes list"]
    },
    {
      id: "gif-store-switch",
      title: "Switch Stores",
      description: "Browse different stores",
      gifUrl: null,
      placeholderType: "gif",
      duration: "4s",
      category: "stores",
      feature: "switch",
      steps: ["Click store selector", "Choose store", "View new deals"]
    },
    {
      id: "gif-roundup",
      title: "Charity Round-Up",
      description: "Donate spare change",
      gifUrl: null,
      placeholderType: "gif",
      duration: "3s",
      category: "checkout",
      feature: "roundup",
      steps: ["Toggle round-up", "See donation amount", "Checkout"]
    }
  ],
  admin: [
    {
      id: "gif-resolve-incident",
      title: "Resolve Incident",
      description: "Close an incident report",
      gifUrl: null,
      placeholderType: "gif",
      duration: "5s",
      category: "incidents",
      feature: "resolve",
      steps: ["Select incident", "Add resolution notes", "Mark resolved"]
    },
    {
      id: "gif-approve-retailer",
      title: "Approve Retailer",
      description: "Verify and approve application",
      gifUrl: null,
      placeholderType: "gif",
      duration: "4s",
      category: "approvals",
      feature: "approve",
      steps: ["Review application", "Check documents", "Click Approve"]
    }
  ],
  charity: [
    {
      id: "gif-view-donations",
      title: "View Donations",
      description: "Check donation history",
      gifUrl: null,
      placeholderType: "gif",
      duration: "4s",
      category: "dashboard",
      feature: "donations",
      steps: ["Open dashboard", "View totals", "See breakdown"]
    }
  ]
};

// =============================================================================
// INTERACTIVE WALKTHROUGHS (Step-by-step guided tours)
// =============================================================================
export const INTERACTIVE_WALKTHROUGHS = {
  retailer: [
    {
      id: "walkthrough-first-post",
      title: "Post Your First Deal",
      description: "Step-by-step guide to posting your first RSHD",
      estimatedTime: "5 min",
      steps: [
        {
          id: "step-1",
          target: "[data-testid='scan-product-btn']",
          title: "Start Scanning",
          content: "Click here to open the barcode scanner and start adding products.",
          position: "bottom",
          action: "click",
          highlightPadding: 8
        },
        {
          id: "step-2",
          target: "[data-testid='single-scan-tab']",
          title: "Choose Scan Mode",
          content: "Single scan is perfect for beginners. Click to select it.",
          position: "bottom",
          action: "click"
        },
        {
          id: "step-3",
          target: "[data-testid='camera-feed']",
          title: "Scan a Barcode",
          content: "Point your camera at a product barcode. The scanner will automatically detect it.",
          position: "top",
          action: "wait",
          waitFor: "scan-complete"
        },
        {
          id: "step-4",
          target: "[data-testid='deal-level-selector']",
          title: "Set Deal Level",
          content: "Choose a discount level: Sizzling (50%), Hot (60%), or Sizzling Hot (75%).",
          position: "left",
          action: "select"
        },
        {
          id: "step-5",
          target: "[data-testid='add-to-queue-btn']",
          title: "Add to Queue",
          content: "Click to add this item to your posting queue.",
          position: "top",
          action: "click"
        },
        {
          id: "step-6",
          target: "[data-testid='queue-review-btn']",
          title: "Review & Post",
          content: "Once you have 2+ items, click here to review and post your deals.",
          position: "left",
          action: "click",
          condition: "queue.length >= 2"
        }
      ],
      completionMessage: "Congratulations! You've posted your first deals. They're now visible to local consumers.",
      relatedDocs: ["retailer-barcode-scanning", "retailer-item-posting"]
    },
    {
      id: "walkthrough-order-flow",
      title: "Handle Your First Order",
      description: "Learn the complete order fulfillment process",
      estimatedTime: "3 min",
      steps: [
        {
          id: "step-1",
          target: "[data-testid='orders-nav']",
          title: "Go to Orders",
          content: "Click Orders in the navigation to see incoming orders.",
          position: "right",
          action: "click"
        },
        {
          id: "step-2",
          target: "[data-testid='pending-order-card']",
          title: "View Pending Order",
          content: "This order needs your acknowledgment. Click to see details.",
          position: "bottom",
          action: "click"
        },
        {
          id: "step-3",
          target: "[data-testid='acknowledge-btn']",
          title: "Acknowledge Order",
          content: "Click Acknowledge to confirm you received the order and commit to the ready-by time.",
          position: "left",
          action: "click"
        },
        {
          id: "step-4",
          target: "[data-testid='mark-ready-btn']",
          title: "Mark Ready",
          content: "Once prepared, click Mark Ready. This captures payment and notifies the customer.",
          position: "left",
          action: "click"
        },
        {
          id: "step-5",
          target: "[data-testid='dispatch-btn']",
          title: "Dispatch Order",
          content: "Click Dispatch to send via DoorDash. The customer gets tracking info automatically.",
          position: "left",
          action: "click"
        }
      ],
      completionMessage: "Great job! You've completed your first order. Keep an eye on the Orders page for new orders.",
      relatedDocs: ["retailer-order-management"]
    }
  ],
  consumer: [
    {
      id: "walkthrough-first-purchase",
      title: "Make Your First Purchase",
      description: "Find deals and complete your first order",
      estimatedTime: "4 min",
      steps: [
        {
          id: "step-1",
          target: "[data-testid='browse-deals-btn']",
          title: "Browse Deals",
          content: "Start by browsing deals near you. Click to explore.",
          position: "bottom",
          action: "click"
        },
        {
          id: "step-2",
          target: "[data-testid='deal-card']",
          title: "View a Deal",
          content: "Click on any deal card to see item details and savings.",
          position: "right",
          action: "click"
        },
        {
          id: "step-3",
          target: "[data-testid='add-to-cart-btn']",
          title: "Add to Cart",
          content: "Like what you see? Click Add to Cart.",
          position: "left",
          action: "click"
        },
        {
          id: "step-4",
          target: "[data-testid='cart-icon']",
          title: "View Cart",
          content: "Click the cart icon to review your items.",
          position: "bottom",
          action: "click"
        },
        {
          id: "step-5",
          target: "[data-testid='checkout-btn']",
          title: "Checkout",
          content: "Ready? Click Checkout to complete your purchase.",
          position: "top",
          action: "click"
        }
      ],
      completionMessage: "You did it! Your order is placed. You'll receive updates as the retailer prepares it.",
      relatedDocs: ["consumer-shopping-checkout"]
    },
    {
      id: "walkthrough-ai-shopping",
      title: "Shop with the AI Agent",
      description: "Let AI find the sizzling hot deals for your list",
      estimatedTime: "3 min",
      steps: [
        {
          id: "step-1",
          target: "[data-testid='agent-nav']",
          title: "Open AI Agent",
          content: "Click to open the DealShaq AI shopping assistant.",
          position: "right",
          action: "click"
        },
        {
          id: "step-2",
          target: "[data-testid='agent-input']",
          title: "Enter Your List",
          content: "Type your shopping list. Example: 'eggs, milk, bread, chicken'",
          position: "top",
          action: "type"
        },
        {
          id: "step-3",
          target: "[data-testid='agent-send-btn']",
          title: "Send to Agent",
          content: "Click send. The agent will find the sizzling hot deals across stores.",
          position: "left",
          action: "click"
        },
        {
          id: "step-4",
          target: "[data-testid='agent-results']",
          title: "Review Results",
          content: "The agent shows sourced items. Review substitutions and savings.",
          position: "top",
          action: "wait"
        },
        {
          id: "step-5",
          target: "[data-testid='add-all-btn']",
          title: "Add All to Cart",
          content: "Happy with the selection? Add all items to your cart at once.",
          position: "left",
          action: "click"
        }
      ],
      completionMessage: "Amazing! The AI found the sizzling hot deals for your list. Shopping made easy!",
      relatedDocs: ["consumer-dealshaq-agent"]
    }
  ],
  admin: [
    {
      id: "walkthrough-incident-resolution",
      title: "Resolve an Incident",
      description: "Handle a platform incident from start to finish",
      estimatedTime: "2 min",
      steps: [
        {
          id: "step-1",
          target: "[data-testid='incidents-nav']",
          title: "Go to Incidents",
          content: "Click Incidents to see active incident reports.",
          position: "right",
          action: "click"
        },
        {
          id: "step-2",
          target: "[data-testid='incident-card']",
          title: "Select Incident",
          content: "Click on an incident to see details and take action.",
          position: "bottom",
          action: "click"
        },
        {
          id: "step-3",
          target: "[data-testid='resolution-textarea']",
          title: "Add Resolution",
          content: "Document what you did to resolve the issue.",
          position: "top",
          action: "type"
        },
        {
          id: "step-4",
          target: "[data-testid='resolve-btn']",
          title: "Mark Resolved",
          content: "Click to close the incident.",
          position: "left",
          action: "click"
        }
      ],
      completionMessage: "Incident resolved! The system logs your resolution for audit purposes.",
      relatedDocs: ["admin-incident-management"]
    }
  ],
  charity: [
    {
      id: "walkthrough-dashboard",
      title: "Explore Your Dashboard",
      description: "Learn what your charity dashboard offers",
      estimatedTime: "2 min",
      steps: [
        {
          id: "step-1",
          target: "[data-testid='donation-total']",
          title: "Total Charitable Contributions",
          content: "This shows your Total Charitable Contributions received through DealShaq.",
          position: "bottom",
          action: "highlight"
        },
        {
          id: "step-2",
          target: "[data-testid='pending-payout']",
          title: "Pending Payout",
          content: "Donations awaiting the next monthly payout on the 15th.",
          position: "bottom",
          action: "highlight"
        },
        {
          id: "step-3",
          target: "[data-testid='impact-metrics']",
          title: "Impact Metrics",
          content: "See how many meals and families your donations have supported.",
          position: "left",
          action: "highlight"
        }
      ],
      completionMessage: "Now you know your way around! Check back regularly to track your impact.",
      relatedDocs: ["charity-donation-dashboard", "charity-impact-metrics"]
    }
  ]
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get video tutorials for a portal
 */
export const getVideoTutorials = (portal) => {
  return VIDEO_TUTORIALS[portal] || [];
};

/**
 * Get GIF demos for a portal
 */
export const getGifDemos = (portal, category = null) => {
  const demos = GIF_DEMOS[portal] || [];
  if (category) {
    return demos.filter(d => d.category === category);
  }
  return demos;
};

/**
 * Get interactive walkthroughs for a portal
 */
export const getWalkthroughs = (portal) => {
  return INTERACTIVE_WALKTHROUGHS[portal] || [];
};

/**
 * Get all multimedia for a documentation section
 */
export const getMultimediaForDoc = (docId) => {
  const result = {
    videos: [],
    gifs: [],
    walkthroughs: []
  };
  
  // Search all portals
  Object.keys(VIDEO_TUTORIALS).forEach(portal => {
    VIDEO_TUTORIALS[portal].forEach(video => {
      if (video.relatedDocs?.includes(docId)) {
        result.videos.push({ ...video, portal });
      }
    });
  });
  
  Object.keys(INTERACTIVE_WALKTHROUGHS).forEach(portal => {
    INTERACTIVE_WALKTHROUGHS[portal].forEach(walkthrough => {
      if (walkthrough.relatedDocs?.includes(docId)) {
        result.walkthroughs.push({ ...walkthrough, portal });
      }
    });
  });
  
  return result;
};

export default {
  VIDEO_TUTORIALS,
  GIF_DEMOS,
  INTERACTIVE_WALKTHROUGHS,
  getVideoTutorials,
  getGifDemos,
  getWalkthroughs,
  getMultimediaForDoc
};
